/**
 * Composition hooks that combine the signed-in user with workspace state.
 *
 * Live in `shared/hooks/` because they cross the boundary between two
 * different `entities/` slices (`entities/user` + `entities/workspace`).
 * Per FSD rules, sibling entities cannot import each other, so any hook
 * that derives data from both must live one layer up.
 *
 * Single-entity selectors live with their owning slice — see
 * `@/entities/user` for organization and user hooks
 * (`useCurrentOrganizationName`, `getCurrentOrganizationId`, etc.).
 *
 * Relocated from `app/hooks/use-workspace-auth.ts` per FSD compliance pass
 *  so that consuming features no longer need a cross-app import.
 */
// Cross-entity aggregator: combines `useCurrentUser` (entities/user) and
// `useCurrentWorkspaceId` (entities/workspace) to derive workspace
// name/slug. FSD prohibits cross-entity value imports between sibling
// entity slices, so this aggregator MUST live above the entity layer.
// Putting it in `widgets/` would block features from consuming it
// (features → widgets is also banned), and there is no app-shell-only
// consumer set, so `shared/` is the pragmatic home. The
// `shared/hooks/useWorkspaceAuth/` directory is classified under the
// `shared-cross-entity-aggregator` element type in `eslint.config.js`,
// which grants it (and ONLY it) `shared → entities` value imports.
import { useCurrentUser } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';

/** Returns the current workspace name by combining user + workspace state. */
export const useCurrentWorkspaceName = () => {
  const currentWorkspaceId = useCurrentWorkspaceId();
  const user = useCurrentUser();
  return user?.workspaces.find((workspace) => workspace.id === currentWorkspaceId)?.name;
};

/** Returns the current workspace slug by combining user + workspace state. */
export const useCurrentWorkspaceSlug = () => {
  const currentWorkspaceId = useCurrentWorkspaceId();
  const user = useCurrentUser();
  return user?.workspaces.find((workspace) => workspace.id === currentWorkspaceId)?.slug;
};

/** Returns the current workspace logo URL (or null) by combining user + workspace state. */
export const useCurrentWorkspaceLogoUrl = () => {
  const currentWorkspaceId = useCurrentWorkspaceId();
  const user = useCurrentUser();
  return user?.workspaces.find((workspace) => workspace.id === currentWorkspaceId)?.logoUrl ?? null;
};
